import React, { useCallback, useMemo, useState } from 'react';
import {
  ApplicationView,
  StartControllerScope,
  useController,
} from '@aha-app/mvc';
import AhaEditorFieldV2Controller, {
  type AhaEditorFieldV2ControllerProps,
} from './controllers/AhaEditorFieldV2Controller';
import { runCallback } from '../aha_editor_field_v2/callbacks';
import {
  AhaEditorV2,
  tools,
  menus,
  ToolbarGroup,
  type Behaviors,
  type EditorPlugin,
  type AhaEditorV2Props,
} from '@aha-app/aha-editor-v2';
import classNames from 'classnames';
import ToolInsertWhiteboard from './plugins/EmbeddedWhiteboard/tools/ToolInsertWhiteboard';
import ToolInsertReport from './plugins/EmbeddedReport/tools/ToolInsertReport';
import AttachmentsController from './controllers/AttachmentsController';
import ToolShowHistory from './plugins/ShowHistory/tools/ToolShowHistory';
import SanitizeInnerHTML from 'components/SanitizeInnerHTML/SanitizeInnerHTML';
import ErrorHandler from './ErrorHandler';
import ToolInsertNewWhiteboard from './plugins/EmbeddedWhiteboard/tools/ToolInsertNewWhiteboard';
import ToolInsertExistingWhiteboard from './plugins/EmbeddedWhiteboard/tools/ToolInsertExistingWhiteboard';
import ToolInsertTemplate from './plugins/InsertTemplate/tools/ToolInsertTemplate';
import useStickyToolbar from './hooks/useStickyToolbar';
import ToolAddSnippetPromo from './plugins/SharedSnippets/tools/ToolAddSnippetPromo';
import {
  ToolCreateRecordLinkAnnotation,
  ToolRemoveRecordLinkAnnotation,
  ToolOpenRecordLinkAnnotation,
  ToolConvertRecordLinkToReference,
  ToolEditRecordLinkAnnotation,
  ToolbarCreateRecordLinkAnnotation,
} from './plugins/Annotations/ToolCreateRecordLinkAnnotation';
import ToolUnlockDocument from './plugins/EditLockPlugin/tools/ToolUnlockDocument';

export interface AhaEditorFieldV2Props
  extends AhaEditorFieldV2ControllerProps {}

const commandMenuTools = [
  [
    tools.ToolToggleHeading1,
    tools.ToolToggleHeading2,
    tools.ToolToggleHeading3,
    tools.ToolToggleHeading4,
    tools.ToolToggleHeading5,
    tools.ToolToggleHeading6,
    tools.ToolToggleCode,
    tools.ToolToggleQuote,
  ],
  [tools.ToolBulletedList, tools.ToolNumberedList, tools.ToolCheckList],
  [
    ToolInsertReport,
    tools.ToolInsertMedia,
    tools.ToolInsertLink,
    tools.ToolInsertLinkCard,
    ToolbarCreateRecordLinkAnnotation,
    tools.ToolInsertTable,
    ToolInsertTemplate,
    tools.ToolAddSnippet,
    tools.ToolWritingAssistant,
  ],
  [
    tools.ToolToggleTableOfContents,
    tools.ToolInsertLabelPill,
    tools.ToolInsertInfoBlock,
    tools.ToolInsertNoteBlock,
    tools.ToolInsertTipBlock,
    tools.ToolInsertWarningBlock,
    tools.ToolInsertDividerLine,
  ],
  [ToolInsertNewWhiteboard, ToolInsertExistingWhiteboard],
];

const insertMenu = ToolbarGroup({
  menu: {
    icon: 'fa-regular fa-plus',
    name: 'Insert',
    tools: [
      [
        ToolInsertReport,
        tools.ToolInsertMedia,
        tools.ToolInsertLink,
        tools.ToolInsertLinkCard,
        ToolbarCreateRecordLinkAnnotation,
        tools.ToolInsertTable,
        ToolInsertTemplate,
        ToolInsertWhiteboard,
        tools.ToolAddSnippet,
        ToolAddSnippetPromo,
        tools.ToolInsertEmbed,
      ],
      [tools.ToolCopyFormat, tools.ToolPasteFormat],
      [
        tools.ToolToggleTableOfContents,
        tools.ToolInsertLabelPill,
        tools.ToolInsertCalloutBlock,
        tools.ToolInsertDividerLine,
      ],
      [tools.ToolWordCount],
    ],
  },
});

const ultraCompactInsertMenu = ToolbarGroup({
  menu: {
    icon: 'fa-regular fa-plus',
    name: 'Insert',
    tools: [
      [
        ToolInsertReport,
        tools.ToolInsertMedia,
        tools.ToolInsertLinkCard,
        tools.ToolInsertLink,
        ToolbarCreateRecordLinkAnnotation,
        tools.ToolInsertTable,
        ToolInsertTemplate,
        tools.ToolAddSnippet,
        tools.ToolInsertEmoji,
        ToolShowHistory,
        tools.ToolShowComments,
      ],
      [
        tools.ToolBulletedList,
        tools.ToolNumberedList,
        tools.ToolDefinitionList,
        tools.ToolCheckList,
      ],
      [tools.ToolDecreaseIndent, tools.ToolIncreaseIndent],
      [tools.ToolCopyFormat, tools.ToolPasteFormat],
      [
        tools.ToolToggleTableOfContents,
        tools.ToolInsertLabelPill,
        tools.ToolInsertCalloutBlock,
        tools.ToolInsertDividerLine,
      ],
      [tools.ToolWordCount],
    ],
  },
});

const toolbar: AhaEditorV2Props['toolbar'] = {
  left: [
    menus.ToolbarBlockMenu(),
    tools.ToolBold,
    tools.ToolItalic,
    tools.ToolUnderline,
    menus.ToolbarFormattingMenu(),
    menus.ToolbarAlignmentMenu(),
    menus.ToolbarListMenu(),
    tools.ToolInsertEmoji,
    insertMenu,
    tools.ToolWritingAssistant,
  ],
  right: [ToolShowHistory, tools.ToolShowComments, tools.ToolFullScreen],
};

const compactToolbar: AhaEditorV2Props['compactToolbar'] = {
  left: [
    menus.ToolbarBlockMenu({ compact: true }),
    menus.ToolbarFormattingMenu({ compact: true }),
    menus.ToolbarAlignmentMenu(),
    menus.ToolbarListMenu(),
    tools.ToolInsertEmoji,
    insertMenu,
    tools.ToolWritingAssistant,
  ],
  right: [ToolShowHistory, tools.ToolShowComments, tools.ToolFullScreen],
};

const ultraCompactToolbar: AhaEditorV2Props['ultraCompactToolbar'] = {
  left: [
    menus.ToolbarBlockMenu({ compact: true }),
    menus.ToolbarFormattingMenu({ compact: true }),
    ultraCompactInsertMenu,
  ],
  right: [tools.ToolWritingAssistant, tools.ToolFullScreen],
};

const annotateOnlyToolbar: AhaEditorV2Props['toolbar'] = {
  left: [],
  right: [ToolShowHistory, tools.ToolShowComments, tools.ToolFullScreen],
};

const readOnlyToolbar: AhaEditorV2Props['toolbar'] = {
  left: [ToolUnlockDocument],
  right: [ToolShowHistory, tools.ToolShowComments, tools.ToolFullScreen],
};

const editToolbarProps = {
  toolbar,
  compactToolbar,
  ultraCompactToolbar,
};

const annotateOnlyToolbarProps = {
  toolbar: annotateOnlyToolbar,
  compactToolbar: annotateOnlyToolbar,
  ultraCompactToolbar: annotateOnlyToolbar,
};

const readOnlyToolbarProps = {
  toolbar: readOnlyToolbar,
  compactToolbar: readOnlyToolbar,
  ultraCompactToolbar: readOnlyToolbar,
};

const AhaEditorFieldV2 = (props: AhaEditorFieldV2Props) => {
  const {
    editorMoreApparent,
    hasAnnotations,
    onComponentDidMount,
    inline = false,
    placeholderText,
    focusOnMount,
    setAhaEditor,
    minimumLines,
  } = props;

  // Re-render the editor once the AhaEditor is fully initialized, since that is after
  // element refs have been initialized
  const [, setInitialized] = useState(false);
  const controller = useController(AhaEditorFieldV2Controller);
  const attachmentsController = useController(AttachmentsController);

  const toolbarOptions = useStickyToolbar(controller.container);

  const [plugins] = useState(() => {
    const initPlugin: EditorPlugin = {
      name: 'AhaEditorFieldV2 initialization plugin',
      handlers: {
        onMount(ahaEditor) {
          setInitialized(true);
          setAhaEditor?.(ahaEditor);
        },
      },
    };

    return [
      ...controller.plugins,
      ...attachmentsController.plugins,
      initPlugin,
    ];
  });

  const setContainer = useCallback(
    (container: HTMLDivElement) => {
      attachmentsController.actionSetContainer(container);
      controller.setContainer(container);
      if (onComponentDidMount && container) {
        runCallback(onComponentDidMount, {
          enclosingElement: controller.enclosingElement,
        });
      }
    },
    [controller, onComponentDidMount, attachmentsController]
  );

  const { showBorder } = controller;
  const behaviors: Behaviors = {
    inline,
    showBorder,
    focusOnMount,
  };
  if (hasAnnotations) {
    behaviors.selectionToolbars = [
      {
        tools: [
          tools.ToolCreateCommentAnnotation,
          ToolCreateRecordLinkAnnotation,
          tools.ToolWritingAssistantSelection,
        ],
        position: 'right',
      },
      {
        tools: [
          ToolOpenRecordLinkAnnotation,
          ToolConvertRecordLinkToReference,
          ToolEditRecordLinkAnnotation,
          ToolRemoveRecordLinkAnnotation,
        ],
        position: 'above',
      },
    ];
  }

  if (placeholderText) {
    behaviors.placeholderChildren = (
      <SanitizeInnerHTML
        element={<span className='user-content' />}
        html={placeholderText}
      />
    );
  }

  if (controller.isReadOnly) {
    behaviors.showBorder = false;
  }

  if (minimumLines) {
    behaviors.minimumLines = minimumLines;
  }

  const editorClasses = classNames({
    'aha-editor-field-v2': true,
    'aha-editor-field-v2--editor-more-apparent': editorMoreApparent,
    'js-annotatable': hasAnnotations,
  });

  const { error, annotateOnly } = controller.state;
  const serializerOptions = useMemo(() => {
    if (!controller) return {};
    return {
      allowedMediaRegex: controller.allowedMediaRegex,
      baseURL: window.location.origin,
      enableFontFamilyMark: true,
      enableFontSizeMark: true,
    };
  }, [controller]);

  let toolbarProps = editToolbarProps;
  if (controller.isReadOnly || controller.isLocked) {
    toolbarProps = readOnlyToolbarProps;
  } else if (annotateOnly) {
    toolbarProps = annotateOnlyToolbarProps;
  }

  return (
    <ErrorHandler controller={controller} error={error}>
      <div ref={setContainer} className={editorClasses}>
        <AhaEditorV2
          value={controller.renderValue}
          plugins={plugins}
          collaborationOptions={controller.collaborationOptions}
          fetchUserInfo={controller.fetchUserInfo}
          readOnly={controller.isReadOnly || controller.isLocked}
          interactive={!controller.isReadOnly}
          objectType={props.objectType}
          objectId={props.documentId}
          toolbarOptions={toolbarOptions}
          commandMenuTools={commandMenuTools}
          annotateOnly={annotateOnly}
          serializerOptions={serializerOptions}
          historyURL={props.historyURL}
          behaviors={behaviors}
          parentType={controller.props.parentType}
          parentId={controller.props.parentId}
          pageId={controller.props.pageId}
          {...toolbarProps}
        />
      </div>
    </ErrorHandler>
  );
};

export default StartControllerScope(
  AhaEditorFieldV2Controller,
  StartControllerScope(AttachmentsController, ApplicationView(AhaEditorFieldV2))
);
